import { useCallback, useEffect, useRef } from 'react';

import useElementEvent from '../../hooks/useElementEvent';
import { getScrollableParents } from '../../util/dom';
import { UseLayerPositionOptions } from './types';
import { getLayerPosition } from './utils';

export const useLayerPosition = <
  AnchorElement extends HTMLElement,
  LayerElement extends HTMLElement
>({
  placement = 'bottom-left',
  offsetY,
  isLayerVisible,
}: UseLayerPositionOptions) => {
  const anchorRef = useRef<AnchorElement>(null);
  const layerRef = useRef<LayerElement>(null);
  const scrollableParents = getScrollableParents(anchorRef.current);

  const updateLayerPosition = useCallback(() => {
    if (anchorRef.current && layerRef.current && isLayerVisible) {
      const layerPosition = getLayerPosition({
        anchorRef,
        layerRef,
        placement,
        offsetY,
      });

      if (!layerPosition) return;

      const { top, left } = layerPosition;
      layerRef.current.style.top = `${top}px`;
      layerRef.current.style.left = `${left}px`;
    }
  }, [anchorRef, layerRef, offsetY, placement, isLayerVisible]);

  useEffect(() => {
    updateLayerPosition();
  }, [updateLayerPosition]);

  // 각 이벤트를 브라우저가 실제로 dispatch하는 target에만 구독한다 (ISSUE-111).
  // `scroll`은 스크롤된 노드에서 발생하고 버블링되지 않으므로 document와 스크롤
  // 가능한 각 조상에 모두 필요하다. `resize`는 오직 `window`에서만 발생한다 —
  // document나 HTMLElement에 건 리스너는 브라우저의 resize 경로에 결코 포함되지
  // 않으므로, 그 두 곳의 구독은 죽은 코드였다.
  useElementEvent('scroll', updateLayerPosition);
  useElementEvent('scroll', updateLayerPosition, scrollableParents);
  useElementEvent('resize', updateLayerPosition, window);

  return {
    anchorRef,
    layerRef,
  };
};
